import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, Lock, AlertCircle, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import LegalReacceptanceModal from '../components/LegalReacceptanceModal';
import HireHubLogo from '../components/HireHubLogo';

const LoginPage = () => {
  const { login, completeMfaLogin, refreshLegalCompliance } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // MFA state
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  const getNavigationPath = (user) => {
    if (user.is_platform_owner || user.roles?.includes('platform_owner')) return '/platform-owner';
    if (user.roles?.includes('super_admin')) return '/super-admin';
    if (user.roles?.includes('admin')) return '/admin';
    if (user.host_status === 'active' && !user.is_platform_owner) return '/owner';
    return from;
  };

  const handlePostLogin = (user, legal_compliance) => {
    const isPlatformOperator = user.is_platform_owner || 
      user.roles?.includes('platform_owner') || 
      user.roles?.includes('super_admin') || 
      user.roles?.includes('admin');
    
    if (!isPlatformOperator && legal_compliance && !legal_compliance.is_compliant && legal_compliance.needs_reacceptance?.length > 0) {
      setPendingUser(user);
      setPendingNavigation(getNavigationPath(user));
      setShowLegalModal(true);
      toast.info('Please review and accept the updated legal terms to continue.');
    } else {
      toast.success('Welcome back!');
      navigate(getNavigationPath(user), { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.mfa_required) {
        setMfaToken(result.mfa_token);
        setMfaStep(true);
        setError('');
      } else {
        handlePostLogin(result.user, result.legal_compliance);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await completeMfaLogin(mfaToken, mfaCode);
      handlePostLogin(result.user, result.legal_compliance);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleLegalAccepted = async () => {
    setShowLegalModal(false);
    await refreshLegalCompliance();
    toast.success('Welcome back!');
    if (pendingNavigation) navigate(pendingNavigation, { replace: true });
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <HireHubLogo size="lg" />
        </Link>

        <Card className="border-slate-100 shadow-lg rounded-2xl">
          <CardHeader className="text-center pb-2">
            {mfaStep ? (
              <>
                <div className="flex justify-center mb-2">
                  <div className="w-12 h-12 rounded-full bg-[#004B8D]/10 flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-[#004B8D]" />
                  </div>
                </div>
                <CardTitle className="font-heading text-2xl">Two-Factor Authentication</CardTitle>
                <CardDescription>Enter the 6-digit code from your authenticator app</CardDescription>
              </>
            ) : (
              <>
                <CardTitle className="font-heading text-2xl">Welcome back</CardTitle>
                <CardDescription>Sign in to your account to continue</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="pt-4">
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="login-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}

            {mfaStep ? (
              /* MFA Code Step */
              <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="mfa-form">
                <div>
                  <Label htmlFor="mfa-code">Verification Code</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9A-Za-z]*"
                    maxLength={8}
                    placeholder="000000"
                    className="h-12 rounded-lg text-center text-2xl tracking-[0.5em] font-mono mt-1"
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\s/g, ''))}
                    autoFocus
                    required
                    data-testid="mfa-code-input"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    You can also enter a recovery code
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#004B8D] hover:bg-[#003865] h-12 rounded-xl font-medium"
                  disabled={loading || mfaCode.length < 6}
                  data-testid="mfa-submit-btn"
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-sm text-slate-500"
                  onClick={() => { setMfaStep(false); setMfaCode(''); setMfaToken(''); setError(''); }}
                  data-testid="mfa-back-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to login
                </Button>
              </form>
            ) : (
              /* Normal Login Step */
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="email" type="email" placeholder="you@example.com"
                        className="pl-10 h-12 rounded-lg"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        required data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link to="/forgot-password" className="text-sm text-[#004B8D] hover:underline" data-testid="forgot-password-link">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="password" type="password" placeholder="--------"
                        className="pl-10 h-12 rounded-lg"
                        value={formData.password}
                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                        required data-testid="password-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#004B8D] hover:bg-[#003865] h-12 rounded-xl font-medium"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                  <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-slate-500">or continue with</span></div>
                </div>
                <Button type="button" variant="outline" className="w-full h-12 rounded-xl border-slate-200" onClick={handleGoogleLogin} data-testid="google-login-btn">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
                <p className="text-center text-sm text-slate-600 mt-6">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-[#004B8D] font-medium hover:underline">Sign up</Link>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <LegalReacceptanceModal isOpen={showLegalModal} onAccepted={handleLegalAccepted} />
    </div>
  );
};

export default LoginPage;
